const MEASUREMENT_ID = 'G-4DKB0YVBX4'
const CONSENT_KEY = 'acs.analytics-consent.v1'
const HOSTS = ['acslive.org', 'www.acslive.org']
const PRIVATE_PATH = /^\/(?:en\/)?(?:login|verify|profile|admin|onboarding|auth|account)(?:\/|$)/i

// Never forward arbitrary query strings, hashes, IDs or form values.
function safeLocation(value) {
  try {
    const url = new URL(value)
    if (!HOSTS.includes(url.hostname) || PRIVATE_PATH.test(url.pathname)) return null
    if (/@|%40|\d{9,}/i.test(url.pathname)) return null
    return `https://acslive.org${url.pathname}`
  } catch (_) { return null }
}

function safeReferrer(value) {
  try {
    const url = new URL(value)
    // Cross-origin referrers often contain only the Firebase host, not /__/auth.
    if (url.hostname.endsWith('.firebaseapp.com')) return ''
    return HOSTS.includes(url.hostname) ? safeLocation(value) || '' : url.origin
  } catch (_) { return '' }
}

function campaignParams(value) {
  const result = {}
  try {
    const url = new URL(value)
    for (const [key, field] of [['utm_source', 'campaign_source'], ['utm_medium', 'campaign_medium'], ['utm_campaign', 'campaign_name']]) {
      const token = url.searchParams.get(key) || ''
      // Only campaign labels; never forward email addresses or phone-like IDs.
      if (/^[a-zA-Z0-9_-]{1,80}$/.test(token) && !/\d{9,}/.test(token)) result[field] = token
    }
  } catch (_) {}
  return result
}

function createAnalytics(win) {
  let consent = null
  let initialized = false
  let lastLocation = null
  try { consent = win.localStorage.getItem(CONSENT_KEY) } catch (_) {}
  const available = () => HOSTS.includes(win.location.hostname)
  const legacyTag = () => !!win.document.querySelector('script[src*="googletagmanager.com/gtag/js?id=UA-"]')
  const gtag = (...args) => win.gtag(...args)

  function initialize(location) {
    if (initialized) return true
    // The Netlify UA snippet MUST be removed in the same release.
    // Fail closed instead of running two competing trackers.
    if (legacyTag() || !location) return false
    win.dataLayer = win.dataLayer || []
    win.gtag = win.gtag || function () { win.dataLayer.push(arguments) }
    gtag('consent', 'default', {
      analytics_storage: 'denied', ad_storage: 'denied',
      ad_user_data: 'denied', ad_personalization: 'denied'
    })
    gtag('consent', 'update', { analytics_storage: 'granted' })
    gtag('js', new Date())
    gtag('config', MEASUREMENT_ID, {
      send_page_view: false,
      page_location: location,
      page_referrer: safeReferrer(win.document.referrer),
      page_title: location === 'https://acslive.org/' ? 'ACS Live' : new URL(location).pathname,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      ...campaignParams(win.location.href)
    })
    const script = win.document.createElement('script')
    script.id = 'acs-ga4'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`
    win.document.head.appendChild(script)
    initialized = true
    return true
  }

  function pageView() {
    if (!available()) return
    const location = safeLocation(win.location.href)
    // Private routes are not measured; disable automatic engagement there too.
    win[`ga-disable-${MEASUREMENT_ID}`] = consent !== 'granted' || !location
    if (!location) { lastLocation = null; return }
    if (consent !== 'granted' || location === lastLocation || !initialize(location)) return
    const params = {
      page_location: location,
      page_referrer: lastLocation || safeReferrer(win.document.referrer),
      page_title: location === 'https://acslive.org/' ? 'ACS Live' : new URL(location).pathname
    }
    gtag('set', params)
    gtag('event', 'page_view', { ...params, send_to: MEASUREMENT_ID })
    lastLocation = location
  }

  function setConsent(value) {
    if (!['granted', 'denied'].includes(value)) return
    consent = value
    try { win.localStorage.setItem(CONSENT_KEY, value) } catch (_) {}
    win[`ga-disable-${MEASUREMENT_ID}`] = value !== 'granted'
    if (initialized) gtag('consent', 'update', { analytics_storage: value })
    if (value === 'denied') {
      lastLocation = null
      // Remove only first-party GA cookies, never authentication cookies.
      win.document.cookie.split(';').forEach(cookie => {
        const name = cookie.split('=')[0].trim()
        if (!/^_ga(?:_|$)/.test(name)) return
        ;['', '; domain=acslive.org', '; domain=.acslive.org'].forEach(domain => {
          win.document.cookie = `${name}=; Max-Age=0; path=/${domain}; SameSite=Lax`
        })
      })
    }
    pageView()
    win.dispatchEvent(new win.Event('acs:analytics-consent'))
  }

  function trackClick(event) {
    if (consent !== 'granted' || !initialized || !safeLocation(win.location.href)) return
    const anchor = event.target.closest?.('a[href]')
    if (!anchor) return
    let url
    try { url = new URL(anchor.href, win.location.href) } catch (_) { return }
    let action
    if (url.hostname === 'apps.apple.com' && url.pathname.includes('id6654906402')) action = 'app_store'
    if (url.hostname === 'play.google.com' && url.searchParams.get('id') === 'com.acslive.acsmax') action = 'google_play'
    if (!action) return
    gtag('event', 'app_download_click', { store: action, send_to: MEASUREMENT_ID })
  }

  function syncConsent() {
    try { consent = win.localStorage.getItem(CONSENT_KEY) } catch (_) { consent = 'denied' }
    if (initialized) gtag('consent', 'update', { analytics_storage: consent === 'granted' ? 'granted' : 'denied' })
    if (consent !== 'granted') lastLocation = null
    pageView()
    win.dispatchEvent(new win.Event('acs:analytics-consent'))
  }
  return { pageView, setConsent, trackClick, syncConsent, getConsent: () => consent, available }
}

let instance
function getAnalytics() {
  if (typeof window === 'undefined') return null
  if (!instance) instance = createAnalytics(window)
  return instance
}
module.exports = { createAnalytics, getAnalytics, safeLocation, safeReferrer, campaignParams, CONSENT_KEY, MEASUREMENT_ID }
